import {
  faClone,
  faPen,
  faPlus,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import EmptyList from 'components/common/EmptyList';
import FalconComponentCard from 'components/common/FalconComponentCard';
import Flex from 'components/common/Flex';
import DialogModal from 'components/modals/DialogModal';
import Pagination from 'components/pagination';
import FailedRequest from 'components/requests-response/FailedRequest';
import SuccessRequest from 'components/requests-response/SuccessRequest';
import useApi from 'helpers/api';
import { useFormatDateTime } from 'hooks/useFormatDateTime';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { useEffect, useState } from 'react';
import { Button, Form, FormControl, InputGroup } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import { Link, useNavigate } from 'react-router-dom';
import TaskTemplateService from 'services/TaskTemplate.service';

const TaskTemplateRegistration = () => {
  const api = useApi();
  const formatDateTime = useFormatDateTime();
  const [isLoading, setIsLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [showAlertError, setShowAlertError] = useState(false);
  const [error, setError] = useState({});
  const [success, setSuccess] = useState('');
  const [taskTemplates, setTaskTemplates] = useState({
    content: [],
    totalElements: 0
  });
  const [filters, setFilters] = useState({
    page: 0,
    size: 20,
    searchTerm: ''
  });

  const [filterNameCustomer, setFilterNameCustomer] = useState('');

  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const isEmpty = !isLoading && taskTemplates.content.length === 0;

  const getData = async () => {
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const response = await TaskTemplateService.list(queryParams);
      if (response.content) {
        setTaskTemplates(response);
      }
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
      setError({ description: 'Erro ao carregar templates de tarefas' });
      setShowAlertError(true);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    getData();
  }, [filters]);

  const handleSearchChange = e => {
    const searchValue = e.target.value;
    setFilterNameCustomer(searchValue);

    if (searchValue === '') {
      setFilters(prevFilters => ({
        ...prevFilters,
        page: 0,
        searchTerm: ''
      }));
    }
  };

  const handleSearch = () => {
    setFilters(prevFilters => ({
      ...prevFilters,
      page: 0,
      searchTerm: filterNameCustomer
    }));
  };

  const confirmDelete = item => {
    setItemToDelete(item);
    setShowDeleteConfirmModal(true);
  };

  const handleDeleteTask = async () => {
    if (!itemToDelete) return;

    try {
      await TaskTemplateService.delete(itemToDelete.id);
      getData();
      setSuccess('Template excluído com sucesso');
      setShowAlert(true);
      setShowDeleteConfirmModal(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Erro ao excluir template:', error);
      setError({ description: 'Erro ao excluir template' });
      setShowAlertError(true);
      setShowDeleteConfirmModal(false);
      setItemToDelete(null);
    }
  };

  const columns = [
    {
      accessorKey: 'title',
      header: 'Nome',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { id, title } = rowData.row.original;
        return <Link to={`/settings/task-templates/${id}`}>{title}</Link>;
      }
    },
    {
      accessorKey: 'serviceCatalog',
      header: 'Catálogo de serviço',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { serviceCatalog, subcategory, category } = rowData.row.original;
        return serviceCatalog
          ? `${serviceCatalog.name} > ${category.name} > ${subcategory.name}`
          : '-';
      }
    },
    {
      accessorKey: 'applyToAllCustomers',
      header: 'Clientes',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { applyToAllCustomers, customers } = rowData.row.original;
        const customersStr = customers
          .map(customer => customer.name)
          .join(', ');

        if (applyToAllCustomers) {
          return <span className="">Todos os clientes</span>;
        } else if (customers && customers.length > 0) {
          return (
            <div
              title={customersStr}
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '250px'
              }}
            >
              {customersStr}
            </div>
          );
        } else {
          return <span>Associação manual</span>;
        }
      }
    },
    {
      accessorKey: 'inactive',
      header: 'Status',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { inactive } = rowData.row.original;
        return inactive ? (
          <span className="text-danger">Inativo</span>
        ) : (
          <span className="text-success">Ativo</span>
        );
      }
    },
    {
      accessorKey: 'creationDate',
      header: 'Criado em',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { creationDate } = rowData.row.original;
        return creationDate ? formatDateTime(creationDate) : '-';
      }
    },
    {
      accessorKey: 'lastUpdateDate',
      header: 'Atualizado em',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { lastUpdateDate } = rowData.row.original;
        return lastUpdateDate ? formatDateTime(lastUpdateDate) : '-';
      }
    },
    {
      accessorKey: 'actions',
      header: 'Ações',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const item = rowData.row.original;
        return (
          <div className="d-flex gap-1">
            <Link to={`/settings/task-templates/${item.id}`}>
              <button className="btn btn-link btn-sm" title="Editar">
                <FontAwesomeIcon icon={faPen} size="sm" />
              </button>
            </Link>
            <Link to={`/settings/task-templates/${item.id}/clone`}>
              <button className="btn btn-link btn-sm" title="Clonar">
                <FontAwesomeIcon icon={faClone} size="sm" />
              </button>
            </Link>
            <button
              className="btn btn-link btn-sm text-primary"
              onClick={() => confirmDelete(item)}
              title="Excluir"
            >
              <FontAwesomeIcon icon={faTrash} size="sm" />
            </button>
          </div>
        );
      }
    }
  ];

  const table = useAdvanceTable({
    data: taskTemplates.content,
    columns
  });

  const navigate = useNavigate();

  return (
    <>
      <SuccessRequest
        message={success}
        showAlert={showAlert}
        setShowAlert={() => setShowAlert(false)}
      />
      <FailedRequest
        message={error.description}
        showAlert={showAlertError}
        setShowAlert={() => setShowAlertError(false)}
      />

      <DialogModal
        showModal={showDeleteConfirmModal}
        handleClose={() => setShowDeleteConfirmModal(false)}
        title="Confirmar exclusão"
        message={`Tem certeza que deseja excluir o template?`}
        response={handleDeleteTask}
      />

      <FalconComponentCard>
        <FalconComponentCard.Header title="Templates de Tarefas">
          <div className="w-100 d-flex justify-content-between">
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate('/settings/task-templates/new')}
            >
              <FontAwesomeIcon icon={faPlus} size="sm" className="me-2" />
              Novo Template
            </Button>
          </div>
        </FalconComponentCard.Header>
        <FalconComponentCard.Body>
          <Flex justifyContent="between" className="mb-3">
            <InputGroup className="position-relative input-search-width">
              <FormControl
                size="sm"
                id="search"
                type="search"
                className="shadow-none"
                placeholder="Buscar por nome"
                value={filterNameCustomer}
                onChange={handleSearchChange}
                style={{ borderRadius: '1px 0px 0px 1px' }}
              />
              <Button
                size="sm"
                variant="outline-secondary"
                className="border-300 hover-border-secondary"
                onClick={handleSearch}
              >
                <FontAwesomeIcon icon="search" className="fs-10" />
              </Button>
            </InputGroup>

            <Form.Select
              size="sm"
              style={{ width: '200px' }}
              className="rounded-pill"
              onChange={e =>
                setFilters(prevFilters => ({
                  ...prevFilters,
                  includeInactives: e.target.value
                }))
              }
            >
              <option value="true">Filtro por status</option>
              <option value="true">Todos</option>
              <option value="false">Somente ativos</option>
            </Form.Select>
          </Flex>

          {isLoading ? (
            <Skeleton count={10} height={44} baseColor="#5E6E82" />
          ) : isEmpty ? (
            <div style={{ minHeight: '300px' }}>
              <EmptyList imgSize={500} vhSize={73} />
            </div>
          ) : (
            <>
              <AdvanceTableProvider {...table}>
                <AdvanceTable
                  table
                  headerClassName="text-nowrap align-middle"
                  rowClassName="align-middle white-space-nowrap"
                  tableProps={{
                    striped: true,
                    className: 'fs-10 mb-0 overflow-hidden'
                  }}
                />
              </AdvanceTableProvider>
              <Pagination
                data={taskTemplates}
                filters={filters}
                setFilters={setFilters}
              />
            </>
          )}
        </FalconComponentCard.Body>
      </FalconComponentCard>
    </>
  );
};

export default TaskTemplateRegistration;
